import { useState } from 'react'
import { X, Loader2, Lock } from 'lucide-react'
import { apiErrorMessage, getTiktokBrandAuthUrl } from '../lib/api'
import { useBrand } from '../hooks/useBrand'

interface Props {
  brandId: string
  onClose: () => void
  /** Optional feature label (e.g. "sync orders"). When set, the modal
   *  swaps its generic copy for a "Required to <feature>" headline so
   *  the user knows which gated action triggered the prompt. */
  feature?: string
}

/**
 * Modal that pitches connecting the brand's official TikTok Shop. Used in
 * two ways:
 *   1. Auto-opened by TiktokConnectProvider on every page load when the
 *      brand has not connected yet (dismissable).
 *   2. Opened on-demand by features that need the shop (via
 *      `requireConnection({ feature })`) — `feature` is shown in the copy.
 *
 * Clicking "Connect" calls /api/v1/tiktok/auth/brand/info/supabase to get
 * an authorize URL, then redirects the browser to TikTok. After the user
 * grants access, TikTok hits /api's callback which forwards to the worker
 * (handleTiktokCallback) and that lands them on /settings?tiktok=connected.
 */
export default function ConnectTikTokModal({ brandId, onClose, feature }: Props) {
  // Connecting a shop is an operational write, so viewers can't do it — the
  // worker's assertBrandCanEdit rejects them. Same gate Settings puts on its
  // own Connect button; without it here, a viewer got the modal (auto-opened
  // by TiktokConnectProvider, or from a gated feature), clicked the CTA and
  // was handed a 403. Explain who can connect instead of offering an action
  // that can only fail.
  const { canEdit } = useBrand()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleConnect = async () => {
    if (!canEdit) return
    setLoading(true)
    setError('')
    try {
      const url = await getTiktokBrandAuthUrl(brandId)
      window.location.href = url
    } catch (e: unknown) {
      // apiErrorMessage, not e.message: the latter is the response body
      // verbatim, which is how {"error":"forbidden"} ended up on screen.
      setError(apiErrorMessage(e, 'Could not start the TikTok connect flow.'))
      setLoading(false)
    }
  }

  return (
    <div style={styles.backdrop} onClick={onClose} role="presentation">
      <div className="connect-tiktok-modal" style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
          <X size={18} />
        </button>

        <div style={styles.iconWrap}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.66a8.16 8.16 0 0 0 4.77 1.52V6.72a4.85 4.85 0 0 1-1.84-.03Z"
              fill="currentColor"
            />
          </svg>
        </div>

        {/* Headline says what's true for THIS reader: an invitation to act for
            someone who can, a statement of what's missing for someone who
            can't (there's no button under it for them). */}
        <h2 style={styles.heading}>
          {feature
            ? canEdit
              ? `Connect TikTok Shop to ${feature}`
              : `TikTok Shop is needed to ${feature}`
            : canEdit
              ? 'Connect your TikTok Shop'
              : 'TikTok Shop isn’t connected'}
        </h2>
        <p style={styles.subheading}>
          {feature
            ? canEdit
              ? `This feature needs your official TikTok Shop linked. Connect now to keep going — you can always come back to it from Settings.`
              : `This feature needs your brand's official TikTok Shop linked. It isn't connected yet.`
            : `Link your official TikTok Shop so we can pull live shop performance, run hires through your seller account, and stay in sync with your product catalog.`}
        </p>

        <ul style={styles.bullets}>
          <li style={styles.bulletItem}>Sync shop performance into your dashboard</li>
          <li style={styles.bulletItem}>Use your seller account for creator hires</li>
          <li style={styles.bulletItem}>Track creator sample requests and fulfillment status</li>
          <li style={styles.bulletItem}>Keep product catalog and orders in one place</li>
        </ul>

        {!canEdit && (
          <div style={styles.noticeBox}>
            <Lock size={13} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Only admins or editors can change integrations. Ask a brand admin to
              connect the shop — it switches on for everyone on the brand once they do.
            </span>
          </div>
        )}

        {error && (
          <div style={styles.errorBox}>{error}</div>
        )}

        <div className="connect-tiktok-actions" style={styles.actions}>
          <button onClick={onClose} style={styles.btnSecondary} disabled={loading}>
            {/* "Maybe later" promises a choice a viewer doesn't have. */}
            {canEdit ? 'Maybe later' : 'Close'}
          </button>
          {canEdit && (
            <button onClick={handleConnect} style={styles.btnPrimary} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  Redirecting…
                </>
              ) : (
                'Connect TikTok Shop'
              )}
            </button>
          )}
        </div>

        {canEdit && (
          <div style={styles.footnote}>
            You'll be redirected to TikTok to authorize. We never see your
            password — only the shop access TikTok grants us.
          </div>
        )}
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 200,
    background: 'rgba(6, 6, 12, 0.72)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  modal: {
    width: '100%',
    maxWidth: '460px',
    background: '#121218',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '18px',
    padding: '32px',
    boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
    position: 'relative' as const,
    color: 'var(--text-primary)',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
  },
  closeBtn: {
    position: 'absolute' as const,
    top: 14,
    right: 14,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '8px',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: 'var(--text-tertiary)',
  },
  iconWrap: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: 'rgba(251, 58, 174, 0.12)',
    border: '1px solid rgba(251, 58, 174, 0.25)',
    color: 'var(--accent)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 18,
  },
  heading: {
    margin: 0,
    fontSize: 20,
    fontWeight: 650,
    letterSpacing: '-0.02em',
  },
  subheading: {
    margin: '8px 0 0',
    fontSize: 14,
    color: 'var(--text-secondary)',
    lineHeight: 1.55,
  },
  bullets: {
    listStyle: 'none',
    padding: 0,
    margin: '20px 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  bulletItem: {
    fontSize: 13,
    color: 'var(--text-secondary)',
    paddingLeft: 18,
    position: 'relative' as const,
  },
  noticeBox: {
    marginTop: 20,
    padding: '11px 13px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.09)',
    borderRadius: 10,
    fontSize: 12.5,
    lineHeight: 1.5,
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
  },
  errorBox: {
    marginTop: 16,
    padding: '10px 12px',
    background: 'rgba(239, 68, 68, 0.08)',
    border: '1px solid rgba(239, 68, 68, 0.25)',
    borderRadius: 10,
    fontSize: 13,
    color: '#fca5a5',
  },
  actions: {
    display: 'flex',
    gap: 10,
    marginTop: 24,
  },
  btnSecondary: {
    flex: '0 0 auto',
    padding: '11px 18px',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnPrimary: {
    flex: 1,
    padding: '11px 18px',
    background: 'var(--accent)',
    color: 'white',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  footnote: {
    marginTop: 18,
    fontSize: 11.5,
    color: 'var(--text-tertiary)',
    lineHeight: 1.5,
  },
}
